/*
  Мелкие части, из которых собраны все экраны.

  Здесь же выполняются два требования Главы 4, которые иначе разъезжаются по
  страницам: сообщение об ошибке всегда показывает и что случилось, и что
  делать (§4.5.6), а пустой экран объясняет, что здесь появится и когда
  (§4.5.4). Формулировку «нет данных» без объяснения конституция запрещает.
*/

import { Link } from 'react-router-dom'

import { money } from '../api/format.js'
import InfoHint from './InfoHint.jsx'
import Modal from './Modal.jsx'
import Dropdown from './Select.jsx'

/*
  Подтверждение необратимого действия (09.09.2026, решение владельца по замене
  ссылки должнику).

  Заведено как общая часть, а не внутри одного экрана: спрашивать «точно?»
  придётся везде, где действие ломает то, что человек уже отправил наружу, и
  два разных окна для одного смысла читались бы как разные вещи.

  Каркас окна - `Modal.jsx`; здесь остаётся то, чем подтверждение отличается от
  остальных окон. Два правила чек-листа формы соблюдены именно здесь, а не в
  вызывающем экране: ошибка живёт ВНУТРИ окна (иначе за окном её не видно, и
  человек считает, что ничего не произошло) и кнопка называется по тому, что
  сделает.
*/
export function Confirm({ title, action, onConfirm, onCancel, busy = false, error, children }) {
  return (
    <Modal title={title} onClose={onCancel} busy={busy}>
      <div className="modal__text">{children}</div>
      <Notice error={error} />
      <div className="row row--tight">
        <button className="button" type="button" onClick={onConfirm} disabled={busy}>
          {action}
        </button>
        <button className="button button--quiet" type="button" onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </Modal>
  )
}

export function Notice({ error, kind = 'error', wide = false, children }) {
  if (!error && !children) return null
  const tone = error ? 'error' : kind
  return (
    <div
      className={`notice notice--${tone}${wide ? ' notice--wide' : ''}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      {error ? (
        <>
          <div className="notice__what">{error.message}</div>
          <div className="notice__do">{error.remedy}</div>
        </>
      ) : (
        children
      )}
    </div>
  )
}

/*
  «Что делать дальше» - отдельно от предупреждения (09.09.2026, решение
  владельца по итогам осмотра).

  До этого совет радара («В первоочередную подачу») и вывод по сроку
  предъявления («Можно предъявлять») выводились жёлтой плашкой `notice--warn`.
  Ни то, ни другое предупреждением не является: первое - рекомендация, второе -
  подтверждение. Цвет тревоги, потраченный на спокойное состояние, перестаёт
  работать - и настоящую потерю денег никто не замечает.
*/
export function Advice({ children }) {
  if (!children) return null
  return (
    <p className="advice">
      <span className="advice__label">дальше</span>
      <span>{children}</span>
    </p>
  )
}

export function Empty({ title, children, footer }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      <div>{children}</div>
      {footer ? <div className="muted">{footer}</div> : null}
    </div>
  )
}

/*
  Плитка денег. `to` превращает её в ссылку - 29.09.2026, указание владельца:
  «хотелось бы кликнуть и увидеть в реестре только эти счета».

  Ссылкой, а не кнопкой с переходом: иначе пропадут средний клик, «открыть в
  новой вкладке» и слово «ссылка» у читалки экрана (правило 6б скилла
  `frontend`). Вид у неё при этом кнопочный, поэтому `.card--link` внесён в
  селектор подъёма при наведении руками - сам он туда не попадает.

  🔴 Ссылка ставится только там, где отбор реестра даёт РОВНО то множество,
  которое посчитала плитка. Приблизительный отбор хуже отсутствующего: человек
  сверит число на плашке с числом найденного и перестанет верить обоим.
*/
/*
  `short` - сумма без копеек. По умолчанию выключен (аудит 26.10.2026, расхождение 14):
  правило «рубли с копейками» (§4.5.1), а округлённая плитка рядом с точной суммой на том
  же экране читалась как две разные цифры - «20 546 990 ₽» и «20 546 989,83 ₽». Сокращение
  допустимо только там, где точная сумма стоит рядом (подпись столбика графика).
*/
export function MoneyCard({ label, value, note, tone, short = false, to }) {
  const body = (
    <>
      <div className="money__label">{label}</div>
      <div className={`money__value${tone === 'burn' ? ' money__value--burn' : ''}`}>
        {money(value, { short })}
      </div>
      {note ? <div className="money__note">{note}</div> : null}
    </>
  )

  if (!to) return <div className="card">{body}</div>
  return (
    <Link className="card card--link" to={to} title={`Открыть в реестре: ${label}`}>
      {body}
    </Link>
  )
}

/*
  Флажок: подпись-обёртка ловит нажатие на всю зону в сорок четыре пикселя, сам
  квадрат нарисован двадцатью (§4.8). Родной браузерный флажок - тринадцать на
  тринадцать, и в конвейере их сорок семь штук.

  `title` обязателен: у флажка в таблице нет видимой подписи рядом, и без него
  читалка экрана называет его просто «флажок».
*/
export function Check({ checked, onChange, title, indeterminate = false, disabled = false }) {
  return (
    <label className="check" title={title}>
      <input
        type="checkbox"
        className="check__box"
        checked={checked}
        disabled={disabled}
        aria-label={title}
        ref={(el) => {
          // Состояние «выбрано не всё» задаётся только свойством узла:
          // атрибута разметки у него нет, и через JSX его не передать.
          if (el) el.indeterminate = indeterminate
        }}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  )
}

/*
  Выбор одного значения из нескольких. Пара обычных кнопок, из которых одна
  залита, читается как два действия: в кабинете залитая кнопка везде означает
  «сделать», и понять, что здесь она означает «выбрано сейчас», неоткуда.
*/
export function Switch({ value, onChange, options, disabled = false }) {
  return (
    <div className="switch" role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`switch__option${option.value === value ? ' switch__option--on' : ''}`}
          aria-pressed={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.title}
        </button>
      ))}
    </div>
  )
}

export function Tag({ kind = 'muted', large = false, children }) {
  return <span className={`tag tag--${kind}${large ? ' tag--lg' : ''}`}>{children}</span>
}

export function Loading({ what = 'Загружаем данные' }) {
  // Прогресс описан по-человечески, а не процентом без смысла (§4.5.5).
  return <div className="muted">{what}…</div>
}

/*
  Поле формы. `width` - ширина по длине ожидаемого значения, а не по месту на
  экране: `xs` для числа и процента, `s` для телефона, ИНН и даты, `m` для
  имени и почты, `l` для адреса. Без неё поле занимает всю ширину обёртки - так
  и было до 09.09.2026, и поле кода региона со значением «42» имело тысячу
  восемьдесят шесть пикселей.

  `hint` - объяснение, ЗАЧЕМ поле нужно, под значком «i» за подписью (у галочки
  - за квадратиком). Строкой под полем пояснения в три-четыре строки делали
  форму текстом, среди которого терялись поля (02.10.2026, указание владельца).

  `error` - причина, по которой сервер не принял ИМЕННО это поле (25.09.2026).
  Приходит с сервера перечнем «поле - причина» и ставится под полем, а не одной
  общей фразой наверху: общая фраза говорит, что форма не сохранилась, и молчит
  о том, где именно искать ошибку. `errorId` нужен, чтобы поле сослалось на этот
  текст через `aria-describedby` - читалка экрана назовёт причину вместе с
  подписью поля, а не оставит человека искать её глазами.
*/
export function Field({ label, hint, width, className, id, error, errorId, children }) {
  /*
    🔴 Пояснение лежит СНАРУЖИ подписи, а не внутри неё.

    Внутри `<label>` весь текст становится названием поля для читалки экрана:
    поле «ИНН» с пояснением называлось бы «ИНН Без него заявление вернут», и
    человек, идущий по форме голосом, слушал бы предложение вместо названия.
    Поймано прогоном 09.09.2026 - `getByLabelText('Файл выгрузки')` перестал
    находить поле ровно потому же, почему его перестала находить бы читалка.

    Поэтому обёртка - `div`, а подпись с полем внутри неё - `label`: связь
    подписи с полем остаётся неявной, как была, а пояснение в название не
    входит. Значок «i» по той же причине стоит ПОСЛЕ `label`; в строку с
    подписью его ставит оформление (`.field--hinted`).
  */
  return (
    <div
      id={id}
      className={`field${width ? ` field--${width}` : ''}${error ? ' field--invalid' : ''}${
        hint ? ' field--hinted' : ''
      }${className ? ` ${className}` : ''}`}
    >
      {/* `id` нужен якорем: с других экранов сюда ведут ссылкой «Изменить»,
          чтобы человек попадал на само поле, а не на вкладку настроек вообще. */}
      <label className="field__control">
        <span className="field__label">{label}</span>
        {children}
      </label>
      {hint ? <InfoHint text={hint} /> : null}
      {error ? (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function Select({ value, onChange, options, empty = 'все' }) {
  return <Dropdown value={value} onChange={onChange} options={options} empty={empty} />
}
